// ═══════════════════════════════════════════════════════════════
// devCockpitApi.js — Client /api/dev/* (Module ITRIIA DEV Cockpit)
// ═══════════════════════════════════════════════════════════════

const getToken = () => localStorage.getItem('oracle_token');

export class DevCockpitApiError extends Error {
    constructor(status, code, message) {
        super(message || code || `HTTP ${status}`);
        this.name = 'DevCockpitApiError';
        this.status = status;
        this.code = code;
    }
}

async function request(path, { method = 'GET', body, timeoutMs = 15000 } = {}) {
    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), timeoutMs);
    try {
        const res = await fetch(path, {
            method,
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${getToken()}`,
            },
            body: body ? JSON.stringify(body) : undefined,
            signal: ctl.signal,
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
            throw new DevCockpitApiError(
                res.status,
                data.error || data.code,
                data.message || data.error || `HTTP ${res.status}`,
            );
        }
        return data;
    } finally {
        clearTimeout(timer);
    }
}

export const getStatus = () => request('/api/dev/status');

export const getRunners = () => request('/api/dev/runners');

export const createProbeRun = (runnerId) =>
    request('/api/dev/runs', { method: 'POST', body: { runner_id: runnerId, mode: 'probe' } });

export const createVersionRun = (runnerId) =>
    request('/api/dev/runs', { method: 'POST', body: { runner_id: runnerId, mode: 'version' } });

export const getRun = (runId) => request(`/api/dev/runs/${runId}`);

// --- Workspace (phase 3) ---

export const getWorkspace = () => request('/api/dev/workspace');

export const initWorkspace = () =>
    request('/api/dev/workspace/init', { method: 'POST', timeoutMs: 130000 });

export const getWorkspaceTree = (path = '', shallow = true) =>
    request(`/api/dev/workspace/tree?path=${encodeURIComponent(path)}&shallow=${shallow ? 'true' : 'false'}`);

export const readWorkspaceFile = (filePath) =>
    request(`/api/dev/workspace/file?path=${encodeURIComponent(filePath)}`);

export const writeWorkspaceFile = (filePath, content) =>
    request('/api/dev/workspace/file', { method: 'PUT', body: { path: filePath, content } });

// --- Git / Checkpoints (phase 4) ---

export const getGitStatus = () => request('/api/dev/git/status');

export const getGitDiff = (filePath = '', staged = false) => {
    const params = new URLSearchParams();
    if (filePath) params.set('path', filePath);
    if (staged) params.set('staged', 'true');
    const qs = params.toString();
    return request(`/api/dev/git/diff${qs ? `?${qs}` : ''}`);
};

export const getFileAtHead = (filePath) =>
    request(`/api/dev/git/file-at-head?path=${encodeURIComponent(filePath)}`);

export const listCheckpoints = () => request('/api/dev/checkpoints');

export const createCheckpoint = (message, label) =>
    request('/api/dev/checkpoints', { method: 'POST', body: { message, label } });

export const restoreCheckpoint = (checkpointId) =>
    request(`/api/dev/checkpoints/${checkpointId}/restore`, { method: 'POST', timeoutMs: 30000 });

export const pushToGitHub = () =>
    request('/api/dev/github/push', { method: 'POST', timeoutMs: 90000 });

export function getTerminalWsUrl() {
    const token = getToken();
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    return `${proto}//${host}/api/dev/terminal?token=${encodeURIComponent(token || '')}`;
}

// --- Preview application (phase 3) ---

export const getPreviewStatus = () => request('/api/dev/preview/status');

export function getPreviewFrameUrl() {
    const token = getToken();
    return `/api/dev/preview/frame?token=${encodeURIComponent(token || '')}`;
}

/**
 * Ouvre un EventSource-like stream via fetch (auth Bearer requis).
 * Retourne { close } pour annuler.
 */
export function streamRun(runId, { onEvent, onError, onDone } = {}) {
    const token = getToken();
    const ctl = new AbortController();

    fetch(`/api/dev/runs/${runId}/stream`, {
        headers: { Authorization: `Bearer ${token}`, Accept: 'text/event-stream' },
        signal: ctl.signal,
    }).then(async (res) => {
        if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            onError?.(new DevCockpitApiError(res.status, data.error, data.message || data.error));
            return;
        }
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        const parseBlock = (block) => {
            const lines = block.split('\n');
            let event = 'message';
            let data = '';
            for (const line of lines) {
                if (line.startsWith('event: ')) event = line.slice(7);
                if (line.startsWith('data: ')) data = line.slice(6);
            }
            if (!data) return;
            try {
                const parsed = JSON.parse(data);
                onEvent?.(event, parsed);
                if (event === 'done' || event === 'error') onDone?.(event, parsed);
            } catch { /* ignore */ }
        };

        while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const parts = buffer.split('\n\n');
            buffer = parts.pop() || '';
            parts.forEach(parseBlock);
        }
        if (buffer.trim()) parseBlock(buffer);
        onDone?.('close');
    }).catch((err) => {
        if (err.name !== 'AbortError') onError?.(err);
    });

    return { close: () => ctl.abort() };
}
